import React, { useEffect, useRef, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import { Redirect, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { useVoiceAgent } from '../../hooks/useVoiceAgent'
import { useUser } from '../../hooks/useUser'
import { useVoiceAgentStyles } from './useVoiceAgentStyles'
import { Routes } from '../../navigation/config/routes'
import {
    VoiceAgentNumber,
    VoiceAgentSetupDataPatch,
    VoiceAgentTone,
} from '../../models/VoiceAgent'
import { CommunitySettings } from '../../main/containers/settings/PropertyDetails/CommunitySettings'
import {
    EDITABLE_FIELDS,
    EditableFieldKey,
    OPTIONAL_FIELDS,
    SETUP_STEPS,
    TONE_OPTIONS,
    fieldsFromSetupData,
    formatE164,
    type FieldDef,
} from './constants'
import { DocumentsSection } from './components/DocumentsSection'
import { LeasingSeasonNotice } from './components/LeasingSeasonNotice'
import { ServiceRequestToggle } from './components/ServiceRequestToggle'
import {
    officeHoursAreSet,
    useCommunityProfile,
} from '../../hooks/useCommunityProfile'

export const VoiceAgentSetupWizard: React.FC = () => {
    const classes = useVoiceAgentStyles()
    const history = useHistory()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id
    const { profile: communityProfile } = useCommunityProfile(apartmentId)
    const {
        setupData,
        agentConfig,
        isLoading,
        error,
        refetch,
        saveSetupData,
        saveAgent,
        provisionNumber,
    } = useVoiceAgent()

    const [activeStep, setActiveStep] = useState(0)
    const [fields, setFields] = useState<Record<EditableFieldKey, string>>(
        {} as Record<EditableFieldKey, string>,
    )
    const [agentName, setAgentName] = useState('Alex')
    const [tone, setTone] = useState<VoiceAgentTone>('warm')
    const [transferPhone, setTransferPhone] = useState('')
    const [provisionedNumber, setProvisionedNumber] =
        useState<VoiceAgentNumber | null>(null)
    const [copied, setCopied] = useState(false)
    const agentHydrated = useRef(false)

    useEffect(() => {
        if (!setupData) return
        setFields(fieldsFromSetupData(setupData))
        if (!agentHydrated.current) {
            setAgentName(
                agentConfig?.agent_name ?? setupData.agent_name ?? 'Alex',
            )
            setTone(agentConfig?.tone ?? setupData.tone ?? 'warm')
            const savedTransfer =
                agentConfig?.human_transfer_e164 ??
                setupData.human_transfer_e164 ??
                ''
            setTransferPhone(savedTransfer ? formatE164(savedTransfer) : '')
            agentHydrated.current = true
        }
        if (setupData.number && !provisionedNumber) {
            setProvisionedNumber(setupData.number)
        }
    }, [setupData, agentConfig, provisionedNumber])

    if (isLoading) {
        return (
            <Box className={classes.emptyRoot}>
                <CircularProgress />
            </Box>
        )
    }

    if (error && !setupData) {
        return (
            <Box className={classes.emptyRoot}>
                <Box className={`${classes.card} ${classes.emptyCard}`}>
                    <Typography
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        Unable to load setup. Please try again.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnPrimary}
                        onClick={() => refetch()}
                    >
                        Retry
                    </Button>
                </Box>
            </Box>
        )
    }

    if (!setupData) {
        return (
            <Box className={classes.emptyRoot}>
                <Typography color="textSecondary">
                    Unable to load setup. Please try again.
                </Typography>
            </Box>
        )
    }

    if (setupData.status === 'live' || setupData.status === 'paused') {
        return <Redirect to={Routes.manage.voiceAgent.root} />
    }

    if (setupData.status === 'pending' && activeStep !== 3) {
        return <Redirect to={Routes.manage.voiceAgent.root} />
    }

    const missingFields = setupData.missing_required_fields ?? []
    const hasOfficeHours =
        officeHoursAreSet(communityProfile?.office_hours) ||
        !!setupData.office_hours?.trim()
    const canAdvanceStep1 = hasOfficeHours

    const handleFieldChange = (key: EditableFieldKey, value: string) => {
        setFields((prev) => ({ ...prev, [key]: value }))
    }

    const handleSaveStep1 = async () => {
        const patch: VoiceAgentSetupDataPatch = { confirm: true }
        EDITABLE_FIELDS.forEach(({ key }) => {
            patch[key] = fields[key] ?? ''
        })
        try {
            const result = await saveSetupData.mutateAsync(patch)
            if (result.missing_required_fields?.length) {
                toast.error(
                    `Please fill required fields: ${result.missing_required_fields.join(
                        ', ',
                    )}`,
                )
                return
            }
            setActiveStep(1)
        } catch {
            // toast handled by hook
        }
    }

    const handleSaveStep2 = async () => {
        try {
            await saveAgent.mutateAsync({
                agent_name: agentName,
                tone,
                human_transfer_e164: transferPhone,
            })
            setActiveStep(2)
        } catch {
            // toast handled by hook
        }
    }

    const handleContinueDocuments = async () => {
        setActiveStep(3)
        if (!provisionedNumber && !setupData.number) {
            try {
                const result = await provisionNumber.mutateAsync()
                setProvisionedNumber(result.number)
            } catch {
                // toast handled by hook
            }
        }
    }

    const displayNumber = provisionedNumber ?? setupData.number ?? null

    const handleCopyNumber = async () => {
        if (!displayNumber?.e164) return
        await navigator.clipboard.writeText(displayNumber.e164)
        setCopied(true)
        toast.success('Copied')
        window.setTimeout(() => setCopied(false), 2000)
    }

    const handleRetryProvision = async () => {
        try {
            const result = await provisionNumber.mutateAsync()
            setProvisionedNumber(result.number)
        } catch {
            // toast handled by hook
        }
    }

    const isFieldMissing = (key: EditableFieldKey) => {
        return missingFields.includes(key) && !fields[key]?.trim()
    }

    const renderField = (field: FieldDef) => {
        const { key, label, placeholder, multiline, helper } = field
        const isMissing = isFieldMissing(key)
        return (
            <Box
                key={key}
                className={classes.fieldGroup}
                style={multiline ? { gridColumn: '1 / -1' } : undefined}
            >
                <TextField
                    label={label}
                    placeholder={placeholder}
                    value={fields[key] ?? ''}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    multiline={multiline}
                    rows={multiline ? 3 : undefined}
                    error={isMissing}
                    InputLabelProps={{ shrink: true }}
                    helperText={helper}
                />
            </Box>
        )
    }

    const renderStep1 = () => (
        <Box>
            <Typography className={classes.stepTitle}>
                Property knowledge
            </Typography>
            <Typography className={classes.stepLead}>
                Community details on the left are what the agent says about
                the property. Agent phrasing and reference documents on the
                right are specific to this voice line.
            </Typography>

            <Box className={classes.knowledgeSplit}>
                <Box className={classes.knowledgeSplitSide}>
                    {apartmentId != null && (
                        <CommunitySettings
                            apartmentId={apartmentId}
                            embedded
                        />
                    )}
                </Box>
                <Box className={classes.knowledgeSplitSide}>
                    <LeasingSeasonNotice />
                    <Box className={classes.fieldSection}>
                        <Typography className={classes.fieldSectionTitle}>
                            Agent phrasing
                        </Typography>
                        <Typography className={classes.fieldSectionDesc}>
                            These lines are only for how the agent talks.
                        </Typography>
                        <Box className={classes.fieldGrid}>
                            {OPTIONAL_FIELDS.map((f) => renderField(f))}
                        </Box>
                    </Box>
                    <DocumentsSection
                        totalChars={setupData.documents?.total_chars ?? 0}
                        docCount={setupData.documents?.count ?? 0}
                    />
                </Box>
            </Box>

            {!hasOfficeHours && (
                <Typography color="error" style={{ marginTop: 8 }}>
                    Set office hours on the left before continuing. The agent
                    and tour booking need them.
                </Typography>
            )}

            <Box className={classes.stepActions}>
                <Button
                    className={classes.btnGhost}
                    onClick={() => history.push(Routes.manage.voiceAgent.root)}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.btnPrimary}
                    onClick={handleSaveStep1}
                    disabled={!canAdvanceStep1 || saveSetupData.isLoading}
                >
                    {saveSetupData.isLoading ? 'Saving...' : 'Continue'}
                </Button>
            </Box>
        </Box>
    )

    const renderStep2 = () => (
        <Box>
            <Typography className={classes.stepTitle}>
                Name, tone, and service requests
            </Typography>
            <Typography className={classes.stepLead}>
                How the agent introduces itself on the phone, and whether it
                can file maintenance tickets for current residents.
            </Typography>

            <Box className={classes.fieldGroup}>
                <TextField
                    label="Agent name"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value.slice(0, 50))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    helperText={`${agentName.length}/50`}
                />
            </Box>

            <Typography className={classes.sectionLabel}>Tone</Typography>
            <Box className={classes.toneGrid}>
                {TONE_OPTIONS.map((opt) => (
                    <Box
                        key={opt.value}
                        className={clsx(
                            classes.toneCard,
                            tone === opt.value && classes.toneCardSelected,
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => setTone(opt.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setTone(opt.value)
                            }
                        }}
                    >
                        <Typography className={classes.toneName}>
                            {opt.label}
                        </Typography>
                        <Typography className={classes.toneDesc}>
                            {opt.description}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Typography className={classes.sectionLabel}>
                Maintenance requests
            </Typography>
            <ServiceRequestToggle />

            <Typography
                className={classes.sectionLabel}
                style={{ marginTop: 24 }}
            >
                Human transfer number
            </Typography>
            <Typography
                color="textSecondary"
                style={{ fontSize: 13, marginBottom: 8 }}
            >
                Optional. If a caller asks for a person twice, the agent
                transfers them here. Do not use a number that forwards
                back to the voice agent.
            </Typography>
            <Box className={classes.fieldGroup}>
                <TextField
                    label="Transfer phone number"
                    placeholder="(512) 555-0199"
                    value={transferPhone}
                    onChange={(e) => setTransferPhone(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 40 }}
                />
            </Box>

            <Box className={classes.stepActions}>
                <Button
                    className={classes.btnGhost}
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(0)}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.btnPrimary}
                    onClick={handleSaveStep2}
                    disabled={saveAgent.isLoading || provisionNumber.isLoading}
                >
                    {saveAgent.isLoading || provisionNumber.isLoading
                        ? 'Saving...'
                        : 'Continue'}
                </Button>
            </Box>
        </Box>
    )

    const renderStep3 = () => {
        if (provisionNumber.isLoading && !displayNumber) {
            return (
                <Box className={classes.provisionLoading}>
                    <CircularProgress />
                    <Typography style={{ marginTop: 16 }} color="textSecondary">
                        Getting your number...
                    </Typography>
                </Box>
            )
        }

        if (!displayNumber) {
            return (
                <Box className={classes.provisionLoading}>
                    <Typography
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        We couldn&apos;t get a number. Try again, or contact
                        support if this keeps happening.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnPrimary}
                        onClick={handleRetryProvision}
                        disabled={provisionNumber.isLoading}
                    >
                        Try again
                    </Button>
                    <Box className={classes.stepActions}>
                        <Button
                            className={classes.btnGhost}
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setActiveStep(2)}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            )
        }

        return (
            <Box>
                <Typography className={classes.stepTitle}>
                    Your number
                </Typography>
                <Typography className={classes.stepLead}>
                    Forward your office line here. The agent answers those
                    calls.
                </Typography>

                <Box className={classes.numberHero}>
                    <Typography className={classes.numberDisplay}>
                        {formatE164(displayNumber.e164)}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnPrimary}
                        startIcon={<FileCopyOutlinedIcon />}
                        onClick={handleCopyNumber}
                        aria-label="Copy phone number"
                    >
                        {copied ? 'Copied' : 'Copy number'}
                    </Button>
                    <Typography className={classes.numberHint}>
                        Most phone systems call this unconditional forwarding
                        (*72), or a forwarding rule in the admin portal.
                    </Typography>
                </Box>

            <Box className={classes.stepActions}>
                <Button
                    className={classes.btnGhost}
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(2)}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.btnPrimary}
                    onClick={() =>
                        history.push(Routes.manage.voiceAgent.root)
                    }
                >
                    Done
                </Button>
            </Box>
        </Box>
        )
    }

    const renderDocumentsStep = () => (
        <Box>
            <Typography className={classes.stepTitle}>
                Reference documents
            </Typography>
            <Typography className={classes.stepLead}>
                Optional. Upload files the agent can use on calls, or skip
                and add them later. This does not block getting a number.
                Do not upload rent sheets or pet addenda — those already
                come from the current leasing season.
            </Typography>

            <DocumentsSection
                hideHeader
                totalChars={setupData.documents?.total_chars ?? 0}
                docCount={setupData.documents?.count ?? 0}
            />

            <Box className={classes.stepActions}>
                <Button
                    className={classes.btnGhost}
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(1)}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.btnPrimary}
                    onClick={handleContinueDocuments}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    )

    return (
        <Box className={classes.root}>
            <Box className={classes.pageHeader}>
                <Box className={classes.headerLeft}>
                    <Typography className={classes.title}>
                        Set up Voice Agent
                    </Typography>
                    <Typography className={classes.pageSubtitle}>
                        Four steps — documents are optional
                    </Typography>
                </Box>
            </Box>

            <Box
                className={
                    activeStep === 0
                        ? classes.setupContent
                        : classes.content
                }
            >
                <Box
                    className={
                        activeStep === 0
                            ? classes.setupWide
                            : classes.wizardRoot
                    }
                >
                    <Box className={classes.stepPills}>
                        {SETUP_STEPS.map((label, index) => (
                            <Box
                                key={label}
                                className={clsx(
                                    classes.stepPill,
                                    index === activeStep &&
                                        classes.stepPillActive,
                                    index < activeStep && classes.stepPillDone,
                                )}
                            >
                                {index + 1}. {label}
                            </Box>
                        ))}
                    </Box>
                    {activeStep === 0 && renderStep1()}
                    {activeStep === 1 && renderStep2()}
                    {activeStep === 2 && renderDocumentsStep()}
                    {activeStep === 3 && renderStep3()}
                </Box>
            </Box>
        </Box>
    )
}
